const INTERFACE_PREFIX_RE = /^(?:PJSIP|Local|SIP|IAX2)\//;
const WEB_SUFFIX_RE = /-web$/i;

/** Extract the bare extension from an Asterisk interface / channel / member
 *  name. Paired WebRTC endpoints `NNNN-web` collapse onto `NNNN` so that the
 *  SIP softphone and the browser WebRTC tab are perceived as the SAME logical
 *  ramal in the UI. Mirror of normalizeExtension in callcenter-v2/shared. */
export function extractExtension(iface) {
  if (!iface) return null;
  const m = String(iface).match(
    /^(?:[A-Za-z][A-Za-z0-9]*\/)?(\d+)(?:-web)?(?:@[^/,]*)?(?:[/,].*)?$/i,
  );
  return m ? m[1] : null;
}

export function isPjsipInterface(iface) {
  return !!iface && String(iface).startsWith('PJSIP/');
}

/** True when the interface refers to the WebRTC twin of a base extension
 *  (e.g. `PJSIP/4001-web`). Used in the UI to render a "WebRTC" badge on
 *  forensic drill-down views so operators can tell which device answered. */
export function isWebEndpoint(iface) {
  if (!iface) return false;
  const s = String(iface).trim();
  if (!s) return false;
  const tail = s.includes('/') ? s.split('/').pop() : s;
  const head = tail.split('@')[0].split(',')[0];
  return WEB_SUFFIX_RE.test(head);
}

/** True when `name` is set and is NOT a raw Asterisk channel string like
 *  `"PJSIP/3530"` or `"Local/3530@mult-ring-rcr/n"`. Used to skip propagating
 *  Asterisk-default placeholders into agent display labels. */
export function isCleanName(name) {
  return !!name && !INTERFACE_PREFIX_RE.test(String(name));
}

export function displayRamal(agent) {
  if (!agent) return '--';
  return (
    agent.extension ||
    extractExtension(agent.interface) ||
    agent.interface ||
    '--'
  );
}

/** Resolves the AGENTE column label: cleaned displayName from DB, else cleaned
 *  memberName from Asterisk, else extension, else `--`. Never returns a value
 *  starting with `PJSIP/`/`Local/`/`SIP/`/`IAX2/`. */
export function displayAgentName(agent) {
  if (!agent) return '--';
  if (isCleanName(agent.displayName)) return agent.displayName;
  if (isCleanName(agent.memberName)) return agent.memberName;
  return agent.extension || extractExtension(agent.interface) || '--';
}

/** True when the agent has a usable identity (an interface containing `/` OR
 *  an extension). Filters out ghost entries from initial AMI QueueMember
 *  enumeration that have neither field populated. */
export function isUsefulAgent(agent) {
  if (!agent) return false;
  const iface = agent.interface || '';
  return iface.includes('/') || !!agent.extension;
}

/** Build a Set of agent extensions that have a connected assignment right now.
 *  Used to derive the "effective in-call" state because the AMI device-state
 *  flag `agent.inCall` is unreliable when the QueueMember is `Local/<ext>` (the
 *  Local channel is necessary for simultaneous SIP+WebRTC ringing but it never
 *  carries device state). Cross-referencing assignments gives a deterministic
 *  source of truth for "agent is on a call right now". */
export function extensionsInActiveAssignments(assignments) {
  const set = new Set();
  if (!assignments) return set;
  const list = Array.isArray(assignments) ? assignments : Object.values(assignments);
  for (const a of list) {
    if (!a || a.status !== 'connected') continue;
    const ext = a.extension || extractExtension(a.interface || '');
    if (ext) set.add(String(ext));
  }
  return set;
}

/** Strip the `{accountcode}-{tenant_slug}-` prefix from a full queue
 *  identifier for display. Falls back to a heuristic that strips just the
 *  accountcode segment when no prefix was supplied by the snapshot. */
export function displayQueue(fullName, namePrefix) {
  if (!fullName) return '--';
  const s = String(fullName);
  if (namePrefix && s.startsWith(namePrefix)) {
    return s.slice(namePrefix.length) || s;
  }
  // Fallback: strip the accountcode-* segment if it looks like one.
  const m = s.match(/^[a-z0-9]{2,5}-(.+)$/i);
  return m ? m[1] : s;
}
